import React from "react";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Motion";
import { CtaBand } from "@/pages/Home";
import founderImg from "@/assets/founder.png";
import refThomson from "@/assets/ref-thomson.png";
import refFaurecia from "@/assets/ref-faurecia.png";
import refGrant from "@/assets/ref-grant.png";
import refTotal from "@/assets/ref-total.png";
import refRiot from "@/assets/ref-riot.png";

const REFS = [
  { src: refThomson, alt: "Thomson Broadcast" },
  { src: refFaurecia, alt: "Faurecia" },
  { src: refGrant, alt: "Grant Thornton" },
  { src: refTotal, alt: "TotalEnergies" },
  { src: refRiot, alt: "Riot Games — Arcane" },
];

const IMG = {
  founder: "https://images.unsplash.com/photo-1604904612715-47bf9d9bc670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwYnVzaW5lc3MlMjB3b21hbiUyMHBvcnRyYWl0JTIwb2ZmaWNlfGVufDB8fHx8MTc4NTI1MTI0NHww&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1706074793638-da28b90ea8ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg1MjUxMjQ0fDA&ixlib=rb-4.1.0&q=85",
};

const About = () => {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow={t.aboutPage.eyebrow} title={t.aboutPage.title} lead={t.aboutPage.lead} />

      {/* Founder editorial */}
      <section className="py-24 lg:py-36 bg-ivory" data-testid="founder-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="clip-editorial overflow-hidden">
                <img src={founderImg} alt={t.founder.name} className="w-full h-[560px] object-cover object-top" data-testid="about-founder-image" />
              </div>
              <div className="absolute bottom-6 -left-4 lg:-left-8 bg-navy text-ivory px-8 py-6 max-w-xs">
                <div className="font-serif text-2xl leading-none text-gold">{t.founder.name}</div>
                <div className="text-[11px] uppercase tracking-[0.14em] mt-3 text-ivory/70">{t.founder.role}</div>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.founder.eyebrow}</span></Reveal>
            <Reveal delay={0.1}>
              <blockquote className="mt-8 font-serif text-3xl lg:text-5xl italic leading-tight text-navy">“{t.founder.quote}”</blockquote>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 text-navy/70 text-lg leading-relaxed max-w-xl">{t.founder.body}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {t.founder.figures.map((f, i) => (
                  <div key={i} className="border-t border-gold/40 pt-4">
                    <div className="font-serif text-3xl lg:text-4xl text-navy leading-none">{f.value}</div>
                    <div className="mt-2 text-[11px] uppercase tracking-[0.12em] text-navy/60">{f.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-10 border-t border-gold/40 pt-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-4">{t.founder.referencesTitle}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {REFS.map((r, i) => (
                    <div key={i} className="flex items-center justify-center bg-white border border-navy/10 px-5 h-20" data-testid={`ref-logo-${i}`}>
                      <img src={r.src} alt={r.alt} title={r.alt} className="max-h-10 w-auto object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Approach / manifesto chapters */}
      <section className="py-24 lg:py-36 bg-navy text-ivory relative overflow-hidden grain" data-testid="approach-section">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12">
          <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.manifesto.eyebrow}</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl lg:text-6xl leading-[1.05] max-w-2xl">{t.manifesto.title}</h2></Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
            {t.manifesto.chapters.map((c, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <div className="bg-navy p-8 lg:p-10 h-full">
                  <span className="font-serif text-6xl text-gold/60">{c.num}</span>
                  <h3 className="mt-8 font-serif text-3xl">{c.title}</h3>
                  <p className="mt-4 text-ivory/60 leading-relaxed text-sm">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-36 bg-ivory" data-testid="values-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <Reveal><h2 className="font-serif text-4xl lg:text-6xl text-navy leading-[1.05]">{t.aboutPage.valuesTitle}</h2></Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 clip-editorial overflow-hidden">
                <img src={IMG.office} alt="" className="w-full h-64 object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-px bg-navy/10 border border-navy/10">
            {t.aboutPage.values.map((v, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <div className="bg-ivory p-8 lg:p-10 h-full">
                  <span className="font-serif text-3xl text-gold">0{i + 1}</span>
                  <h3 className="mt-6 font-serif text-2xl text-navy">{v.title}</h3>
                  <p className="mt-3 text-navy/70 text-sm leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export default About;
